'use client';

import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';

import { EVENT_NAMES, trackEvent } from '@/analytics';
import { Logo } from '@/components/Logo';
import { LinksList } from '@/components/SiteHeader/LinksList';
import { MobileMenuToggleGroup } from '@/components/SiteHeader/MobileMenuToggleGroup';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Typography } from '@/components/Typography';
import List from '@/icons/react/List';
import X from '@/icons/react/X';
import type { SiteNavMenuType } from '@/lib/firestore/dataFetchers/getSiteNavMenu';
import { useSiteHeader, useTheme } from '@/providers';

interface MobileMenuProps {
  topics?: SiteNavMenuType;
}

export function MobileMenu({ topics }: MobileMenuProps) {
  const { siteHeaderMainRef, mobileMenuOpen, setMobileMenuOpen } =
    useSiteHeader();
  const { showToggle } = useTheme();

  const { refs, context } = useFloating({
    open: mobileMenuOpen,
    onOpenChange: setMobileMenuOpen,
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  function handleMenuClick() {
    setMobileMenuOpen(!mobileMenuOpen);

    trackEvent(EVENT_NAMES.opened_mobile_menu);
  }

  const headerRect = siteHeaderMainRef?.current?.getBoundingClientRect();
  const headerYOffset = (headerRect?.top || 0) + (headerRect?.height || 81); // some sane-ish defaults

  return (
    <>
      <button
        className="flex md:hidden items-center justify-center p-3"
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={handleMenuClick}
      >
        <span className="sr-only">Menu</span>
        {mobileMenuOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <List className="w-6 h-6" />
        )}
      </button>

      <FloatingPortal id="floating-ui-portal">
        {mobileMenuOpen && (
          <>
            <FloatingOverlay
              className="bg-black/75 dark:bg-gray-500/75 md:hidden"
              lockScroll
              style={{ top: headerYOffset }}
            />
            <FloatingFocusManager context={context}>
              <div
                className="flex flex-col fixed inset-x-0 bottom-0"
                ref={refs.setFloating}
                {...getFloatingProps()}
                style={{ top: headerYOffset }}
              >
                {/* Menu Content */}
                <div
                  className={classNames(
                    'bg-paper overflow-hidden flex-grow relative',
                    'dark:bg-gray-800',
                  )}
                >
                  <div className="flex flex-col overflow-y-auto absolute inset-0 pb-8">
                    <div className="border-t border-primary flex flex-col py-3">
                      {topics?.map((group, index) => (
                        <MobileMenuToggleGroup
                          key={group.category?.id}
                          category={group.category}
                          childrenTopics={group.childTopics}
                          comingSoonTopics={group.comingSoonTopics}
                          menuIndex={index}
                        />
                      ))}
                    </div>
                    <div className="border-t border-primary flex flex-col">
                      <Typography
                        name="tag-350"
                        className="text-secondary pt-6 px-4"
                      >
                        Helpful Links
                      </Typography>
                      <LinksList />
                    </div>
                    {showToggle && (
                      <div className="px-4 pt-6 flex">
                        <ThemeToggle />
                      </div>
                    )}
                    <div className="px-4 py-6">
                      <Logo className="text-gray-200 fill-current w-20 h-auto" />
                    </div>
                  </div>

                  {/* White fade at bottom */}
                  <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-paper dark:from-gray-800 to-transparent pointer-events-none" />
                </div>

                {/* Menu Footer */}
                <div className="grow-0 shrink-0">
                  <button
                    type="button"
                    onClick={handleMenuClick}
                    className="w-full p-6 flex items-center justify-end gap-2 text-paper"
                  >
                    <Typography name="heading-400">Close menu</Typography>
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </FloatingFocusManager>
          </>
        )}
      </FloatingPortal>
    </>
  );
}
